import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import TextArea from '../../../../components/TextArea';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { Validation } from '../../../../interfaces/common';
import Switch from '../../../../components/Switch';
import { AddEdit as AddEditInterface } from '../../../../interfaces/template.interface';

interface TemplateDetailsTabProps {
    edit: boolean;
    view?: boolean; // view is optional in interface but used as view || false usually, or strict boolean. In older file it was optional? Let's keep it consistent.
    formData: AddEditInterface;
    fileTypes: any[];
    allFileTypes: any[];
    dropdownSearchData: any;
    onDropdownSearchChange: (event: any, name: string) => void;
    validation: Validation;
    onKeyDown: (e: any, name?: string) => void;
    onInputBlur: (e: any, name: string) => void;
    onInputChange: (e: any, name: string) => void;
}

const TemplateDetailsTab: React.FC<TemplateDetailsTabProps> = ({
    edit,
    view,
    formData,
    fileTypes,
    allFileTypes,
    dropdownSearchData,
    onDropdownSearchChange,
    validation,
    onKeyDown,
    onInputBlur,
    onInputChange,
}) => {
    const { t } = useTranslation();


    return (
        <form className="card-body" autoComplete="off">
            <div className="row g-6">
                <h6 className="mb-0">1. {t('templates.addEdit.pageTexts.form.sections.primaryDetails')}</h6>

                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <Input
                            type="text"
                            required
                            id={VALIDATION_FIELD_MAPPING.name}
                            name={VALIDATION_FIELD_MAPPING.name}
                            disabled={view}
                            error={
                                !!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.name)
                            }
                            errorMessage={
                                validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.name)
                                    ?.message ?? ''
                            }
                            label={t('templates.addEdit.pageTexts.form.name.label')}
                            placeholder={t('templates.addEdit.pageTexts.form.name.placeholder')}
                            value={formData.name}
                            onKeyDown={onKeyDown}
                            onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
                            onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.name)}
                        />
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <Input
                            type="text"
                            required
                            id={VALIDATION_FIELD_MAPPING.code}
                            name={VALIDATION_FIELD_MAPPING.code}
                            disabled={view}
                            error={
                                !!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.code)
                            }
                            errorMessage={
                                validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.code)
                                    ?.message ?? ''
                            }
                            label={t('templates.addEdit.pageTexts.form.code.label')}
                            placeholder={t('templates.addEdit.pageTexts.form.code.placeholder')}
                            value={formData.code}
                            onKeyDown={onKeyDown}
                            onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.code)}
                            onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.code)}
                        />
                    </div>
                </div>

                <div className="col-md-6">
                    <Dropdown
                        id={VALIDATION_FIELD_MAPPING.fileType}
                        name={VALIDATION_FIELD_MAPPING.fileType}
                        values={formData.fileType ? [formData.fileType] : []}
                        options={(fileTypes || []).map((item: any) => ({
                            label: item.name,
                            value: item.id,
                        }))}
                        allOptions={(allFileTypes || []).map((item: any) => ({
                            label: item.name,
                            value: item.id,
                        }))}
                        searchText={dropdownSearchData.fileType}
                        onSearch={onDropdownSearchChange}
                        disabled={view}
                        error={
                            !!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.fileType)
                        }
                        errorMessage={
                            validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.fileType)
                                ?.message ?? ''
                        }
                        label={t('templates.addEdit.pageTexts.form.fileType.label')}
                        placeholder={t('templates.addEdit.pageTexts.form.fileType.placeholder')}
                        onChange={onInputChange}
                        onBlur={onInputBlur}
                    />
                </div>
                <h6 className="mb-0 mt-4">
                    2. {t('templates.addEdit.pageTexts.form.sections.otherInformation')}
                </h6>

                <div className="col-md-10">
                    <div className="form-floating form-floating-outline">
                        <TextArea
                            required
                            className="form-control-lg h-100"
                            id={VALIDATION_FIELD_MAPPING.description}
                            name={VALIDATION_FIELD_MAPPING.description}
                            disabled={view}
                            error={
                                !!validation.fields.find(
                                    (field) => field.field === VALIDATION_FIELD_MAPPING.description
                                )
                            }
                            errorMessage={
                                validation.fields.find(
                                    (field) => field.field === VALIDATION_FIELD_MAPPING.description
                                )?.message ?? ''
                            }
                            label={t('templates.addEdit.pageTexts.form.description.label')}
                            placeholder={t('templates.addEdit.pageTexts.form.description.placeholder')}
                            value={formData.description}
                            rows={5}
                            onKeyDown={onKeyDown}
                            onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.description)}
                            onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.description)}
                        />
                    </div>
                </div>

                <div className="col-md-2">
                    <Switch
                        label={t('templates.addEdit.pageTexts.form.status.label')}
                        name={VALIDATION_FIELD_MAPPING.status}
                        checked={formData.status === 'true'} // Assuming status is stored as string 'true'/'false'
                        disabled={view}
                        onChange={(e: any, name: string) => {
                            const value = e.target.checked ? 'true' : 'false';
                            const fakeEvent = { target: { value } };
                            onInputChange(fakeEvent, name);
                        }}
                    />
                </div>
            </div>
        </form>
    );
};

export default TemplateDetailsTab;
